import React, { useEffect, useState } from "react";
import { PhotoCamera, SelectAllRounded } from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { createAdmission, fetchBranches, fetchCourses } from "../../utils/Apis";
import { useDispatch, useSelector } from "react-redux";
import { IconButton, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Grid,
  Button,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  Paper,
  Avatar,
  Stack,
  Link,
  Chip,
} from "@mui/material";
import Loader from "../commonComponents/Loader";
import CourseDetailsModal from "../commonComponents/courseDetailsModal";

const MotionTypography = motion(Typography);

const AdmissionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const { loading, dataError } = useSelector((state) => state.data);
  const { branches, courses } = useSelector((state) => state.data);
  const [selected, setSelected] = useState([]);
  const [discounts, setDiscounts] = useState();
  const [totalFee, setTotalFee] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCourseForInfo, setSelectedCourseForInfo] = useState(null);

  const handleInfoOpen = (course) => {
    setSelectedCourseForInfo(course);
    setModalOpen(true);
  };

  const handleInfoClose = () => {
    setModalOpen(false);
    setSelectedCourseForInfo(null);
  };

  const { status, admissionError } = useSelector((state) => state.admissions);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    fatherName: "",
    motherName: "",
    mobileNo: "",
    emailId: "",
    dob: "",
    permanentAddress: {
      street: "",
      city: "",
      state: "",
      zipcode: "",
    },
    currentAddress: {
      street: "",
      city: "",
      state: "",
      zipcode: "",
    },
    sameAddress: false,
    highestQualification: "",
    courseId: [],
    branchId: [],
    profilePic: null,
    termsAccepted: false,
  });

  useEffect(() => {
    if (!branches.length > 0) dispatch(fetchBranches());
    if (!courses.length > 0) dispatch(fetchCourses());
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(selected) || selected.length === 0) {
      setDiscounts(0);
      setTotalFee(0);
      return;
    }

    let baseTotal = 0;
    let totalDiscount = 0;

    selected.forEach((id) => {
      const course = courses?.courses?.find((c) => c._id === id);
      if (course) {
        baseTotal += course?.feeStructure?.baseFee || 0;
        totalDiscount += course?.feeStructure?.discount || 0;
      }
    });

    setDiscounts(totalDiscount);
    setTotalFee(baseTotal - totalDiscount);
  }, [selected, courses]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "courseId") {
      setSelected(typeof value === "string" ? value.split(",") : value);
    }
  };

  const handleAddressChange = (event, type) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [type]: {
        ...prevData[type],
        [name]: value,
      },
    }));
  };

  const handleFileChange = (event) => {
    if (event.target.files[0]) {
      // Check file size (Max 50KB)
      if (event.target.files[0].size > 2 * 1024 * 1024)
        alert("File size must be less than 2MB!");
      else setFormData({ ...formData, profilePic: event.target.files[0] });
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    if (name === "sameAddress") {
      setFormData((prevData) => ({
        ...prevData,
        sameAddress: checked,
        currentAddress: checked ? prevData.permanentAddress : {},
      }));
    } else {
      setFormData({ ...formData, [name]: checked });
    }
  };

  const validateForm = (data) => {
    const newErrors = {};

    // Personal Information
    if (!data.firstName) newErrors.firstName = "First Name is required";
    if (!data.lastName) newErrors.lastName = "Last Name is required";
    if (!data.fatherName) newErrors.fatherName = "Father's Name is required";
    if (!data.motherName) newErrors.motherName = "Mother's Name is required";

    // Contact Information
    if (!data.mobileNo) newErrors.mobileNo = "Mobile Number is required";
    if (!data.emailId) newErrors.email = "Email is required";

    // Address Information
    if (!data.permanentAddress?.street)
      newErrors.permanentAddressStreet = "Street is required";
    if (!data.permanentAddress?.city)
      newErrors.permanentAddressCity = "City is required";
    if (!data.permanentAddress?.state)
      newErrors.permanentAddressState = "State is required";
    if (!data.permanentAddress?.zipcode)
      newErrors.permanentAddressZipcode = "Zip Code is required";

    // Educational Information
    if (!data.highestQualification)
      newErrors.highestQualification = "highestQualification is required";
    if (!data.courseId) newErrors.courseId = "CourseId is required";
    if (!data.branchId) newErrors.branchId = "BranchId is required";

    // Terms and Conditions
    if (!data.termsAccepted)
      newErrors.termsAccepted = "You must accept the terms and conditions";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validate the form data
    if (!validateForm(formData)) {
      alert("Please fill all the required fields");
      return;
    }
    try {
      let newFormData = new FormData();
      const { profilePic, ...rest } = formData;
      newFormData.append("profilePic", profilePic);
      newFormData.append("formData", JSON.stringify(rest));
      // Dispatch the action to create an admission
      await dispatch(createAdmission(newFormData)).unwrap();

      // Navigate to the thank you page on success
      navigate("/thank-you");
    } catch (err) {
      // Handle errors by showing the error message
      console.error("Error submitting form:", err);
      alert(
        err || "An error occurred while submitting the form. Please try again."
      );
    }
  };

  if (loading) {
    return <Loader fullPage />;
  }

  return (
    <form onSubmit={handleSubmit} encType="multipart/form-data">
      {loading && <div>Loading...</div>}
      {dataError && <div>Error: {dataError}</div>}
      {!loading && !dataError && (
        <Box
          sx={{
            p: 4,
            backgroundColor: "#f5f5f5",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
          }}
        >
          <MotionTypography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: "#333",
              textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
              textAlign: "center",
            }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.8,
              delay: 0.5,
              ease: [0, 0.71, 0.2, 1.01],
            }}
          >
            Your skill needs only one step to upgrade! 🎓
          </MotionTypography>
          <Typography
            sx={{
              textWrap: "wrap",
              textAlign: "center",
              maxWidth: { xs: "95%", md: "50%" },
            }}
          >
            Start your journey with us! Join a community that empowers and
            prepares you for success.
          </Typography>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              padding: 2,
              marginBlock: "2rem",
              backgroundColor: "white",
              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              borderRadius: "16px",
              maxWidth: 800,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: "#333",
                textShadow: "1px 1px 2px rgba(0,0,0,0.1)",
                textAlign: "center",
              }}
            >
              Skill Form
            </Typography>

            {/* Profile Photo and Upload Button */}
            <Stack alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Avatar
                alt="Profile Photo"
                src={
                  formData.profilePic
                    ? URL.createObjectURL(formData.profilePic)
                    : ""
                }
                sx={{ width: 100, height: 100 }}
              />
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{
                  background: "#333",
                  color: "#fff",
                  borderRadius: "22px",
                }}
              >
                Upload Photo
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  name="profilePic"
                  accept=".png, .jpg, .jpeg"
                />
              </Button>
            </Stack>

            <Grid container spacing={3}>
              {/* Personal Information Section */}
              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Personal Information
                </Typography>
                <Box display="flex" gap={2}>
                  <TextField
                    label="First Name"
                    name="firstName"
                    variant="outlined"
                    fullWidth
                    required
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="Last Name"
                    name="lastName"
                    variant="outlined"
                    fullWidth
                    required
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="Father's Name"
                    name="fatherName"
                    variant="outlined"
                    fullWidth
                    required
                    onChange={handleInputChange}
                  />
                </Box>
                <Box display="flex" gap={2} sx={{ mt: 2 }}>
                  <TextField
                    label="Mother's Name"
                    name="motherName"
                    variant="outlined"
                    fullWidth
                    required
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="Date of Birth"
                    name="dob"
                    type="date"
                    variant="outlined"
                    fullWidth
                    onChange={handleInputChange}
                    InputLabelProps={{
                      shrink: true, // ensures label doesn't overlap
                    }}
                  />
                </Box>
              </Grid>

              {/* Contact Information Section */}
              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Contact Information
                </Typography>
                <Box display="flex" gap={2} sx={{ mt: 2 }}>
                  <TextField
                    label="Mobile Number"
                    name="mobileNo"
                    type="number"
                    variant="outlined"
                    fullWidth
                    required="true"
                    onChange={handleInputChange}
                  />
                  <TextField
                    label="EmailId"
                    name="emailId"
                    type="email"
                    variant="outlined"
                    fullWidth
                    required="true"
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              {/* Address Details Section */}
              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Address Details
                </Typography>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Street"
                    name="street"
                    variant="outlined"
                    fullWidth
                    required="true"
                    onChange={(e) => handleAddressChange(e, "permanentAddress")}
                  />
                  <TextField
                    label="City"
                    name="city"
                    variant="outlined"
                    fullWidth
                    required="true"
                    onChange={(e) => handleAddressChange(e, "permanentAddress")}
                  />
                </Box>
                <Box display="flex" gap={2} sx={{ mt: 2 }}>
                  <TextField
                    label="State"
                    name="state"
                    variant="outlined"
                    fullWidth
                    required="true"
                    onChange={(e) => handleAddressChange(e, "permanentAddress")}
                  />
                  <TextField
                    label="Zip Code"
                    name="zipcode"
                    variant="outlined"
                    fullWidth
                    onChange={(e) => handleAddressChange(e, "permanentAddress")}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sameAddress"
                      checked={formData.sameAddress}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label="Same as Permanent Address"
                />
                {!formData.sameAddress && (
                  <Box sx={{ mt: 2 }}>
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Current Street"
                        name="street"
                        variant="outlined"
                        fullWidth
                        onChange={(e) =>
                          handleAddressChange(e, "currentAddress")
                        }
                      />
                      <TextField
                        label="Current City"
                        name="city"
                        variant="outlined"
                        fullWidth
                        onChange={(e) =>
                          handleAddressChange(e, "currentAddress")
                        }
                      />
                    </Box>
                    <Box display="flex" gap={2} sx={{ mt: 2 }}>
                      <TextField
                        label="Current State"
                        name="state"
                        variant="outlined"
                        fullWidth
                        onChange={(e) =>
                          handleAddressChange(e, "currentAddress")
                        }
                      />
                      <TextField
                        label="Current Zip Code"
                        name="zipcode"
                        variant="outlined"
                        fullWidth
                        onChange={(e) =>
                          handleAddressChange(e, "currentAddress")
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Academic Details Section */}
              <Grid item xs={12} sx={{ width: "100%" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Academic Details
                </Typography>
                <Box display="flex" gap={2}>
                  <FormControl fullWidth>
                    <InputLabel>Highest Qualification</InputLabel>
                    <Select
                      name="highestQualification"
                      value={formData.highestQualification}
                      onChange={handleInputChange}
                      label="Highest Qualification"
                    >
                      <MenuItem value="10th">10th or Equivalent</MenuItem>
                      <MenuItem value="12th">12th or Equivalent</MenuItem>
                      <MenuItem value="Graduation">
                        Graduation or Equivalent
                      </MenuItem>
                      <MenuItem value="Post Graduation">
                        Post Graduation or Equivalent
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel required>Select Course</InputLabel>

                    <Select
                      multiple
                      name="courseId"
                      value={selected}
                      onChange={handleInputChange}
                      label="Select Course"
                      renderValue={(selected) => (
                        <Box
                          overflow="hidden"
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((id) => {
                            const course = courses?.courses?.find(
                              (c) => c._id === id
                            );
                            return <Chip key={id} label={course?.name || id} />;
                          })}
                        </Box>
                      )}
                    >
                      {courses?.courses?.map((course) => (
  <MenuItem
    key={course._id}
    value={course._id}
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      p: 1.5,
      gap: 2,
    }}
  >
    {/* Left: Course Name */}
    <Typography>{course.name}</Typography>

    {/* Right Section */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      
      {/* Fees */}
      <Box sx={{ textAlign: "right" }}>
        {course.feeStructure.discount > 0 && (
          <Typography
            sx={{
              textDecoration: "line-through",
              color: "gray",
              fontSize: ".8rem",
            }}
          >
            ₹{course.feeStructure.baseFee}
          </Typography>
        )}

        <Typography sx={{ fontWeight: "bold", color: "#000" }}>
          ₹{course.feeStructure.finalFee}
        </Typography>
      </Box>

      {/* Info Icon with Tooltip */}
      <Tooltip title="Click for details" arrow>
        <IconButton
          onClick={() => handleInfoOpen(course)}
          sx={{
            width: 30,
            height: 30,
            bgcolor: "#f3f3f3",
            "&:hover": { bgcolor: "#e0e0e0" },
          }}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  </MenuItem>
))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel required>Select Nearest Branch</InputLabel>
                    <Select
                      name="branchId"
                      value={formData.branchId}
                      onChange={handleInputChange}
                      label="Select Nearest Branch"
                    >
                      {branches?.branches?.map((branch) => (
                        <MenuItem key={branch._id} value={branch._id}>
                          <div>
                            <p>
                              <strong>{branch.name}</strong>
                            </p>
                            <p>{branch.location.address}</p>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>

              {/* Fee summary */}
              {selected?.length > 0 && (
                // <Box mt={2}>
                //   {discounts > 0 && (
                //     <Typography variant="body1" color="green"><strong>Total Discount:</strong> ₹{discounts}</Typography>
                //   )}
                //   <Typography variant="body1" color="primary"><strong>Final Fee:</strong> ₹{totalFee}</Typography>
                // </Box>
                <Box display="flex" gap={2}>
                  {discounts > 0 && (
                    <TextField
                      label="Your Total Discounts"
                      name="discount"
                      type="number"
                      value={discounts}
                      InputProps={{ readOnly: true }}
                      helperText="Total Discounts of your selected course(s)"
                      fullWidth
                    />
                  )}
                  <TextField
                    label="Total Fee"
                    name="finalFee"
                    type="number"
                    value={totalFee}
                    InputProps={{ readOnly: true }}
                    helperText="Auto-calculated (Base - Discounts)"
                    fullWidth
                  />
                </Box>
              )}

              {/* Terms and Conditions Section */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.termsAccepted}
                      onChange={handleCheckboxChange}
                      name="termsAccepted"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2" component="span">
                        I accept the{" "}
                      </Typography>
                      <Link
                        component={RouterLink}
                        to="/terms-and-conditions"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          ml: 0.5,
                        }}
                      >
                        Terms and Conditions
                      </Link>
                      <Typography variant="body2" component="span">
                        &nbsp;and{" "}
                      </Typography>
                      <Link
                        component={RouterLink}
                        to="/privacy-policy"
                        sx={{
                          textDecoration: "underline",
                          cursor: "pointer",
                          ml: 0.5,
                        }}
                      >
                        Privacy Policy
                      </Link>
                    </Box>
                  }
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}></Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                sx={{
                  background: "#333",
                  borderRadius: "22px",
                }}
              >
                Submit
              </Button>
            </Grid>
          </Box>
        </Box>
      )}
      <CourseDetailsModal
        open={modalOpen}
        handleClose={handleInfoClose}
        course={selectedCourseForInfo}
      />
    </form>
  );
};

export default AdmissionForm;